import { ChevronDown, Code2, Copy, Download, Eye, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useMediaQuery } from '../hooks/useMediaQuery';
import type { Artifact, ArtifactKind } from '../lib/artifacts';
import { cn } from '../lib/cn';
import { downloadWorkspaceFile } from '../lib/files';
import { ArtifactCodeView } from './ArtifactCodeView';
import { ArtifactPreview } from './ArtifactPreview';

type Props = {
  artifacts: Artifact[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
};

type Tab = 'preview' | 'code';

/**
 * Right-side panel listing every artifact the current session
 * emitted. Preview tab holds the HTML/SVG iframe; Code tab shows
 * a shiki-highlighted read-only view. Selector dropdown appears
 * when there's more than one artifact.
 *
 * The panel itself is always visible when mounted — the parent
 * (ChatPage) decides whether to mount it at all.
 */
export function ArtifactsPanel({ artifacts, selectedId, onSelect, onClose }: Props) {
  const current = artifacts.find((a) => a.id === selectedId) ?? artifacts[artifacts.length - 1];
  const [tab, setTab] = useState<Tab>(current?.kind === 'code' ? 'code' : 'preview');
  const [copied, setCopied] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const isOverlay = !useMediaQuery('(min-width: 1024px)');
  useFocusTrap(panelRef, isOverlay);

  useEffect(() => {
    if (!isOverlay) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOverlay, onClose]);

  const currentId = current?.id;
  const currentKind = current?.kind;
  useEffect(() => {
    if (!currentId) return;
    setTab(currentKind === 'code' ? 'code' : 'preview');
    setDownloadError(null);
  }, [currentId, currentKind]);

  async function onCopy(): Promise<void> {
    if (!current) return;
    try {
      await navigator.clipboard.writeText(current.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — swallow */
    }
  }

  async function onDownload(): Promise<void> {
    if (!current) return;
    if (current.relativePath) {
      try {
        setDownloadError(null);
        await downloadWorkspaceFile(current.relativePath);
      } catch (err) {
        setDownloadError(err instanceof Error ? err.message : 'Не удалось скачать файл');
      }
      return;
    }
    const filename = inferFilename(current);
    const mime = mimeForKind(current.kind, current.lang);
    const blob = new Blob([current.body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  if (!current) return null;

  const hasPreview = current.kind === 'html' || current.kind === 'svg' || current.kind === 'binary';
  const hasCode = current.kind !== 'binary';

  return (
    <aside
      ref={panelRef}
      role={isOverlay ? 'dialog' : undefined}
      aria-modal={isOverlay || undefined}
      aria-label="Артефакты"
      className="mc-safe-bottom fixed inset-0 z-40 flex h-full min-w-0 w-full flex-col bg-background lg:static lg:z-auto lg:w-[520px] lg:border-l lg:border-border"
    >
      <header className="mc-safe-top mc-safe-x flex items-center gap-1.5 border-b border-border px-2 py-2">
        <div className="relative min-w-0 flex-1">
          <button
            type="button"
            onClick={() => artifacts.length > 1 && setPickerOpen((v) => !v)}
            className={cn(
              'flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[14px]',
              artifacts.length > 1 && 'hover:bg-subtle',
            )}
            title={current.title ?? current.lang}
          >
            <span className="truncate font-medium">{current.title ?? current.lang}</span>
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
              {current.lang}
            </span>
            {artifacts.length > 1 && (
              <ChevronDown size={12} className="shrink-0 text-muted-foreground" />
            )}
          </button>
          {pickerOpen && artifacts.length > 1 && (
            <div className="absolute top-[calc(100%+4px)] left-0 z-10 min-w-[240px] rounded-2xl border border-border/70 bg-surface p-1.5 mc-float">
              {artifacts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    onSelect(a.id);
                    setPickerOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-subtle',
                    a.id === current.id && 'bg-subtle',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{a.title ?? a.lang}</span>
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                    {a.lang}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {hasPreview && hasCode && (
          <div className="flex shrink-0 rounded-md border border-border p-0.5 text-[12px]">
            <button
              type="button"
              onClick={() => setTab('preview')}
              className={cn(
                'inline-flex items-center gap-1 rounded px-2 py-1',
                tab === 'preview' ? 'bg-subtle' : 'text-muted-foreground hover:bg-subtle',
              )}
              aria-label="Просмотр"
            >
              <Eye size={12} />
              <span className="hidden md:inline">Просмотр</span>
            </button>
            <button
              type="button"
              onClick={() => setTab('code')}
              className={cn(
                'inline-flex items-center gap-1 rounded px-2 py-1',
                tab === 'code' ? 'bg-subtle' : 'text-muted-foreground hover:bg-subtle',
              )}
              aria-label="Код"
            >
              <Code2 size={12} />
              <span className="hidden md:inline">Код</span>
            </button>
          </div>
        )}

        {hasCode && (
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-md p-1.5 text-[12px] text-muted-foreground hover:bg-subtle md:px-2 md:py-1"
            title="Копировать"
            aria-label="Копировать"
          >
            <Copy size={12} />
            <span className="hidden md:inline">{copied ? 'Copied' : 'Copy'}</span>
          </button>
        )}
        {(hasCode || current.relativePath) && (
          <button
            type="button"
            onClick={() => void onDownload()}
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-md p-1.5 text-[12px] text-muted-foreground hover:bg-subtle md:px-2 md:py-1"
            title="Скачать"
            aria-label="Скачать"
          >
            <Download size={12} />
            <span className="hidden md:inline">Скачать</span>
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 min-w-11 shrink-0 rounded-md p-2 text-muted-foreground hover:bg-subtle"
          title="Закрыть панель"
          aria-label="Закрыть панель"
        >
          <X size={14} />
        </button>
      </header>

      {downloadError && (
        <div role="alert" className="border-b border-border px-3 py-2 text-[12px] text-destructive">
          {downloadError}
        </div>
      )}

      <div className="relative flex-1 overflow-hidden overscroll-contain">
        {hasPreview && (tab === 'preview' || !hasCode) ? (
          <ArtifactPreview artifact={current} />
        ) : (
          <ArtifactCodeView artifact={current} />
        )}
      </div>
    </aside>
  );
}

const EXT_FOR_LANG: Record<string, string> = {
  html: 'html',
  htm: 'html',
  svg: 'svg',
  xml: 'xml',
  jsx: 'jsx',
  tsx: 'tsx',
  react: 'tsx',
};

const MIME_FOR_KIND: Record<ArtifactKind, string> = {
  html: 'text/html; charset=utf-8',
  svg: 'image/svg+xml',
  react: 'text/plain; charset=utf-8',
  code: 'text/plain; charset=utf-8',
  binary: 'application/octet-stream',
};

function mimeForKind(kind: ArtifactKind, lang: string): string {
  if (kind === 'code' && lang === 'xml') return 'application/xml';
  return MIME_FOR_KIND[kind] ?? 'text/plain; charset=utf-8';
}

function inferFilename(a: Artifact): string {
  const ext = EXT_FOR_LANG[a.lang] ?? a.lang ?? 'txt';
  if (a.title) {
    // Title might already be a filename (e.g. tool-based artifacts
    // use the Write file_path's basename). If it already ends with
    // a known extension, keep it; otherwise slug + add extension.
    if (/\.[a-z0-9]{1,6}$/i.test(a.title)) return a.title;
    const slug = a.title.replace(/[^\w.()\-]+/g, '_').slice(0, 80) || 'artifact';
    return `${slug}.${ext}`;
  }
  return `artifact-${a.id.replace(/[^\w-]+/g, '_').slice(0, 24)}.${ext}`;
}
